import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'debounce';
import { Action, ActionType, Mode, performAction } from '../api/performAction';
import { getExperienceBasedOnTemperature, showExperienceAnimation } from '../utils';
import { useUserStore } from '../store';
import { shallow } from "zustand/shallow";
import { ParticleSystemUtil } from '../components/GameSceneNew/particles/ParticleSystemUtil';
import { Object3D } from 'three';
import { BatchedRenderer } from 'three.quarks';
import { useTokenStore } from '../store/useTokenStore';

export type Coords = { x: number; y: number };
export const ENERGY_TO_REDUCE_ON_HOLD = 5;

const DEBOUNCE_INTERVAL = 300;

const getKFactor = (happiness: number, hunger: number, health: number): number => {
  if (happiness === 100 && hunger === 100 && health === 100) {
    return 3;
  } else if (happiness > 0 && hunger > 0 && health > 0) {
    return 2;
  } else {
    return 1;
  }
};

export const useUserAction = (mode: Mode) => {
  const [hunger, happiness, health, endurance, energy, temperature] = useUserStore(
    (state) => [
      state.user.hunger,
      state.user.happiness,
      state.user.health,
      state.user.endurance,
      state.user.energy,
      state.user.temperature,
    ],
    shallow
  );
  const temperatureRef = useRef(temperature);

  const token = useTokenStore((state) => state.accessToken);

  const addTemperatureAndReduceEnergy = useUserStore((state) => state.addTemperatureAndReduceEnergy);
  const addExperienceAndReduceEnergy = useUserStore((state) => state.addExperienceAndReduceEnergy);
  const addHappinessAndReduceEnergy = useUserStore((state) => state.addHappinessAndReduceEnergy);

  const sentStartDate = useRef<number | null>(null);
  const holdIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const holdSessionStart = useRef<Date | null>(null);
  const swipeSessionStart = useRef<Date | null>(null);

  const clicks = useRef(0);
  const holds = useRef(0);
  const swipes = useRef(0);

  const clickSessions = useRef<Array<Action>>([]);
  const currentClickSession = useRef<Action | null>(null);

  const clickSessionStart = useRef<Date | null>(null);


  const startClickSession = useCallback(() => {
    clickSessionStart.current = new Date();
    currentClickSession.current = {
      start: clickSessionStart.current.toISOString(),
      end: clickSessionStart.current.toISOString(),
      clicks: 0,
      temperature: temperature ?? 0,
    };
  }, [temperature]);

  const closeClickSession = useCallback(() => {
    if (!currentClickSession.current) return;

    currentClickSession.current.end = new Date().toISOString();
    clickSessions.current.push(currentClickSession.current);

    currentClickSession.current = null;
    clickSessionStart.current = null;
    clicks.current = 0;
  }, []);

  const sendActionsToServer = useCallback(
    (payload: { type: ActionType; callback?: () => void; actions: Array<Action> }) => {
      if (!token) return;
      performAction({ token, mode, ...payload });
    },
    [token, mode],
  );

  const debouncedSendActionsToServer = useMemo(
    () => debounce(sendActionsToServer, DEBOUNCE_INTERVAL),
    [sendActionsToServer],
  );

  const handleAddExperience = useCallback(
    (addedExpAmount: number, energyCost: number, coords?: Coords) => {
      if (energy === null || energy < energyCost || temperature === null) {
        return;
      }

      if (coords && addedExpAmount > 0) {
        showExperienceAnimation(coords, addedExpAmount);
      }

      addExperienceAndReduceEnergy(addedExpAmount, energyCost);
    },
    [addExperienceAndReduceEnergy, energy, temperature],
  );

  const handleClickAction = useCallback(
    (coords: Coords) => {
      if (energy === null || temperature === null) return;

      if (!currentClickSession.current) {
        startClickSession();
      } else {
        if (
          currentClickSession.current.temperature !== temperature
        ) {
          closeClickSession();
          startClickSession();
        }
      }

      if (mode === 'egg') {
        if (energy < 1.0) return;
        const expGain = getExperienceBasedOnTemperature(temperature);
        handleAddExperience(expGain, 1.0, coords);
      } else if (mode === 'owl') {
        if (energy < 1.0) return;
        handleAddExperience(0.0, 1.0, coords);
      } else if (mode === 'dummy') {
        if (energy < 1.0) return;
        const kFactor = getKFactor(happiness, hunger, health);
        handleAddExperience(1.0 * kFactor, 1.0, coords);
      }

      clicks.current += 1;

      if (currentClickSession.current) {
        currentClickSession.current.clicks = clicks.current;
        currentClickSession.current.end = new Date().toISOString();
      }

      debouncedSendActionsToServer({
        type: 'click',
        actions: [
          ...clickSessions.current,
          ...(currentClickSession.current ? [currentClickSession.current] : []),
        ],
        callback: () => {
          clickSessions.current = [];
          currentClickSession.current = null;
          clickSessionStart.current = null;
          clicks.current = 0;
        },
      });
    },
    [
      energy,
      temperature,
      mode,
      startClickSession,
      closeClickSession,
      handleAddExperience,
      debouncedSendActionsToServer,
      happiness,
      hunger,
      health,
    ],
  );

  const handleHoldAction = useCallback(
    (effect: Object3D | undefined, holdCallback?: (time: number) => void) => {
      if (holdIntervalRef.current) {
        clearInterval(holdIntervalRef.current);
      }

      if (energy === null) {
        return;
      }

      if (mode === 'egg') {
        if (!holdSessionStart.current) {
          holdSessionStart.current = new Date();
        }

        holdIntervalRef.current = setInterval(() => {
          if (energy < 5.0) {
            return;
          }

          holds.current += 1;

          addTemperatureAndReduceEnergy(1.0, 5.0);

          if (holdCallback) {
            holdCallback(holds.current);
          }

          if (temperatureRef.current) {
            ParticleSystemUtil.updateHeatEffect(temperatureRef.current, effect);
          }
          const addHeatData = { x: 50, y: 50 };
          const addHeat = new CustomEvent('addHeat', { detail: addHeatData });
          document.dispatchEvent(addHeat);
        }, 1500);
      }
    },
    [addTemperatureAndReduceEnergy, energy, temperature, mode],
  );

  const handleHoldStopAction = useCallback(
    (effect?: Object3D) => {
      if (holdIntervalRef.current) {
        clearInterval(holdIntervalRef.current);

        if (mode === 'egg') {
          ParticleSystemUtil.updateHeatEffect(-1, effect);
        }

        if (holds.current === 0) {
          return;
        }

        const start = holdSessionStart.current;
        if (!start) {
          return;
        }

        if (start.getTime() === sentStartDate.current) {
          return;
        }

        sentStartDate.current = start.getTime();

        sendActionsToServer({
          type: 'hold',
          actions: [
            {
              temperature: temperature,
              start: start.toISOString(),
              end: new Date().toISOString(),
              holds: holds.current,
            },
          ],
          callback: () => {
            holdSessionStart.current = null;
            holds.current = 0;
          },
        });
      }
    },
    [sendActionsToServer, mode],
  );

  const handleSwipeAction = useCallback(
    (
      scene: Object3D,
      effect: Object3D | undefined,
      batchedRenderer: BatchedRenderer,
      coords?: Coords,
      rotation?: number,
    ) => {
      if (energy === null) {
        return;
      }

      if (mode === 'owl') {
        if (energy < 10.0) {
          return;
        }
        addHappinessAndReduceEnergy(1.0, 10.0);
        ParticleSystemUtil.playEffect('HeartPoof', scene, effect, batchedRenderer);
      } else if (mode === 'dummy') {
        if (energy < 5.0) {
          return;
        }
        const kFactor = getKFactor(happiness, hunger, health);
        handleAddExperience(5.0 * kFactor, 5.0, coords);
        ParticleSystemUtil.playEffect('ClawAttack', scene, effect, batchedRenderer, rotation);
      }

      if (!swipeSessionStart.current) {
        swipeSessionStart.current = new Date();
      }

      swipes.current += 1;

      const start = swipeSessionStart.current;
      if (!start) {
        return;
      }

      debouncedSendActionsToServer({
        type: 'swipe',
        actions: [
          {
            temperature: temperature,
            start: start.toISOString(),
            end: new Date().toISOString(),
            swipes: swipes.current,
          },
        ],
        callback: () => {
          swipeSessionStart.current = null;
          swipes.current = 0;
        },
      });
    },
    [
      addHappinessAndReduceEnergy,
      debouncedSendActionsToServer,
      handleAddExperience,
      mode,
      happiness,
      hunger,
      health,
      energy,
    ],
  );

  useEffect(() => {
    temperatureRef.current = temperature;
  }, [temperature]);

  useEffect(() => {
    const onContextMenuTriggered = (event: MouseEvent) => {
      event.preventDefault();
    };
    document.addEventListener('contextmenu', onContextMenuTriggered);

    return () => {
      document.removeEventListener('contextmenu', onContextMenuTriggered);
      if (holdIntervalRef.current) {
        clearInterval(holdIntervalRef.current);
      }
    };
  }, []);

  return useMemo(
    () => ({
      handleClickAction,
      handleHoldAction,
      handleHoldStopAction,
      handleSwipeAction,
    }),
    [handleClickAction, handleHoldAction, handleHoldStopAction, handleSwipeAction],
  );
};
